import { TAppDispatch, TGetState } from 'store/types'
import { getAiVideoVersions_API } from 'api'
import { IVersionHistoryItem } from 'store/types/aiVideo'
import { aiVideoSelectors } from 'store/features/aiVideo/selectors'
import { captureException } from '@sentry/react'
import { ApiError, ReduxThunkError } from 'utils/errors'
import { getStringifiedQuality } from 'store/features/aiVideo/utils'
import { AIVideoSocketMessageStatus } from 'types/aiVideo'
import slice from 'store/features/aiVideo/slice'

const { actions } = slice

const getVersionHistory =
  (assetId: string | number) =>
  async (dispatch: TAppDispatch, getState: TGetState) => {
    // Non-numeric ids (e.g. temporary genAiVideoHash for in-flight regeneration)
    // can't be queried. Mark history as loaded-empty so the empty state renders
    // instead of a white blank.
    if (!assetId || isNaN(Number(assetId))) {
      dispatch(actions.setVersionHistoryItems([]))
      return
    }

    dispatch(actions.setVersionHistoryLoading(true))

    try {
      const { data } = await getAiVideoVersions_API(assetId)
      const models = aiVideoSelectors.aiVideoModels(getState())
      const modelsByCode = new Map(models.map(m => [m.model, m.name]))
      const items: IVersionHistoryItem[] = (data || []).map(video => {
        const isVideo = !!video.videoUrl
        const thumbnailUrl = isVideo
          ? video.imageUrl || video.referenceMedias?.images?.[0]?.url
          : undefined
        return {
          id: video.id,
          url: video.videoUrl || video.imageUrl || '',
          prompt: video.prompt,
          type: isVideo ? ('video' as const) : ('image' as const),
          modelName: modelsByCode.get(video.videoModel) ?? video.videoModel,
          modelQuality: video.quality
            ? getStringifiedQuality(video.quality)
            : undefined,
          durationSeconds: isVideo ? video.videoDurationSeconds : undefined,
          width: video.width,
          height: video.height,
          aspectRatio: video.aspectRatio,
          process: video.status === AIVideoSocketMessageStatus.PROCESSING,
          thumbnailUrl,
          previewPath: video.previewPath
        }
      })
      dispatch(actions.setVersionHistoryItems(items))
    } catch (error) {
      if (!(error instanceof ApiError)) {
        captureException(new ReduxThunkError(error))
      }
      dispatch(actions.setVersionHistoryItems([]))
    } finally {
      dispatch(actions.setVersionHistoryLoading(false))
    }
  }

export const versionHistoryThunks = {
  getVersionHistory
}
